/**
 * Mobile PDF share helpers (Web Share API + coarse mobile detection).
 * Device-agnostic: any coarse/narrow mobile client — not a single OEM/model.
 *
 * Important: do not preview PDFs in an iframe on mobile. iOS Safari/Chrome
 * clip/mis-scale PDF plugins in iframes; the system PDF viewer (new tab /
 * download) is the correct display path.
 */

/**
 * @returns {boolean}
 */
export function isCoarseMobileClient() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const narrow =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 768px)').matches;
  const ua = String(navigator.userAgent || '');
  const uaMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  return Boolean(coarse || (narrow && uaMobile) || uaMobile);
}

/**
 * @returns {boolean}
 */
export function hasWebShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * @param {Blob} blob
 * @param {string} filename
 * @returns {File|null}
 */
export function blobToPdfFile(blob, filename) {
  if (!(blob instanceof Blob)) return null;
  const name = String(filename || 'CyberTracer-report.pdf').replace(/[^\w.\-@+]+/g, '_');
  if (typeof File !== 'undefined') {
    return new File([blob], name, { type: 'application/pdf' });
  }
  return null;
}

/**
 * True when we should offer a Share control (Web Share present + File constructible).
 * Some browsers expose share but return false from canShare({files}); still enable
 * the button and attempt share on click.
 *
 * @param {Blob} blob
 * @param {string} filename
 * @returns {boolean}
 */
export function canNativeSharePdf(blob, filename) {
  if (!hasWebShare()) return false;
  return Boolean(blobToPdfFile(blob, filename));
}

/**
 * @param {Blob} blob
 * @param {string} filename
 * @returns {boolean}
 */
export function canSharePdfFiles(blob, filename) {
  if (!canNativeSharePdf(blob, filename)) return false;
  const file = blobToPdfFile(blob, filename);
  if (!file) return false;
  if (typeof navigator.canShare !== 'function') return true;
  try {
    return Boolean(navigator.canShare({ files: [file] }));
  } catch {
    return false;
  }
}

/**
 * Open the PDF in the OS/browser native viewer (new tab). Prefer this over iframe.
 *
 * @param {Blob} blob
 * @returns {Window|null}
 */
export function openPdfInNativeViewer(blob) {
  if (typeof window === 'undefined' || !(blob instanceof Blob)) return null;
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after the new tab has had time to load the blob URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return win;
}

/**
 * Trigger a classic download of the PDF blob (iOS often opens the system PDF viewer).
 *
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadPdfBlob(blob, filename) {
  if (typeof document === 'undefined' || !(blob instanceof Blob)) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = String(filename || 'CyberTracer-report.pdf');
  a.rel = 'noopener';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Open the OS share sheet with the PDF attached when supported.
 * Falls back to Save + text share when file sharing is refused.
 *
 * @param {object} opts
 * @param {Blob} opts.blob
 * @param {string} opts.filename
 * @param {string} [opts.title]
 * @param {string} [opts.text]
 * @param {boolean} [opts.autoSaveOnFileFail=true]
 * @returns {Promise<'shared'|'cancelled'|'unsupported'|'saved_fallback'|'error'>}
 */
export async function nativeSharePdf({
  blob,
  filename,
  title = 'CyberTracer OSINT report',
  text = 'CyberTracer investigation PDF',
  autoSaveOnFileFail = true,
} = {}) {
  if (!canNativeSharePdf(blob, filename)) return 'unsupported';
  const file = blobToPdfFile(blob, filename);
  if (!file) return 'unsupported';

  const payload = {
    files: [file],
    title: String(title || 'CyberTracer OSINT report'),
    text: String(text || ''),
  };

  try {
    await navigator.share(payload);
    return 'shared';
  } catch (err) {
    const name = err && typeof err === 'object' ? err.name : '';
    if (name === 'AbortError') return 'cancelled';
  }

  if (autoSaveOnFileFail) {
    downloadPdfBlob(blob, filename);
  }

  try {
    await navigator.share({
      title: String(title || 'CyberTracer OSINT report'),
      text: `${String(text || 'CyberTracer investigation PDF')}\n\nPDF saved on this device — attach it from Files/Downloads in WhatsApp or Mail.`,
    });
    return 'saved_fallback';
  } catch (err) {
    const name = err && typeof err === 'object' ? err.name : '';
    if (name === 'AbortError') return 'cancelled';
    return autoSaveOnFileFail ? 'saved_fallback' : 'error';
  }
}
